import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, Alert, Divider, Checkbox } from 'antd';
import { UserAddOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useRegister } from '@digiwedge/hooks-auth-web';

const { Title, Text } = Typography;

interface RegisterFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  givenName: string;
  familyName: string;
  termsAccepted: boolean;
}

export function RegisterPage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { register, loading, error } = useRegister();
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (values: RegisterFormValues) => {
    try {
      await register({
        email: values.email,
        password: values.password,
        givenName: values.givenName,
        familyName: values.familyName,
        preferredName: values.givenName,
        termsAccepted: values.termsAccepted,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: 400, margin: '48px auto', padding: '0 16px' }}>
        <Card>
          <Alert
            type="success"
            message="Registration Successful!"
            description="Your account has been created. Redirecting to login..."
            showIcon
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 400, margin: '48px auto', padding: '0 16px' }}>
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <UserAddOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            Create Account
          </Title>
          <Text type="secondary">Join YOLO Shop to track your orders</Text>
        </div>

        {error && (
          <Alert
            type="error"
            message="Registration Failed"
            description={error.message || 'Unable to create account. Please try again.'}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="givenName"
              label="First Name"
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="John" />
            </Form.Item>
            <Form.Item
              name="familyName"
              label="Last Name"
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="Doe" />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="john@example.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter a password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password placeholder="Minimum 8 characters" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm your password" />
          </Form.Item>

          <Form.Item
            name="termsAccepted"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms to continue')),
              },
            ]}
          >
            <Checkbox>I accept the Terms of Service</Checkbox>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Create Account
            </Button>
          </Form.Item>
        </Form>

        <Divider>or</Divider>

        <div style={{ textAlign: 'center' }}>
          <Text>Already have an account? </Text>
          <Link to="/login">Sign in</Link>
        </div>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Link to="/">
            <Button type="link" icon={<ArrowLeftOutlined />}>
              Back to Shop
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

export default RegisterPage;
